import * as React from 'react';

import { ICON_SIZES } from '../constants';
import { clampButtonText } from '../helpers/clamp-button-text';

import { Styled } from './content-switcher-button.styles';
import type { ContentSwitcherButtonProps } from './content-switcher-button.types';

export const ContentSwitcherButton = React.forwardRef<
  HTMLElement,
  ContentSwitcherButtonProps
>(
  (
    {
      activeItemIndex,
      children,
      href,
      icon: Icon,
      miniBadge,
      onClick,
      role,
      selected = false,
      size,
      sizeConfined,
      sizeWide,
      variant,
      to,
      ...props
    },
    forwardedRef
  ) => {
    const iconSize = size ? ICON_SIZES[size] : size;
    const setRef = React.useCallback(
      (element: HTMLElement | null): void => {
        if (typeof forwardedRef === 'function') forwardedRef(element);
        else if (forwardedRef) forwardedRef.current = element;
      },
      [forwardedRef]
    );

    const commonProps = {
      $selected: selected,
      $size: size,
      $sizeConfined: sizeConfined,
      $sizeWide: sizeWide,
      $variant: variant,
      'aria-selected': selected,
      'data-state': selected ? 'active' : null,
      onClick: (e: React.MouseEvent) => onClick && onClick(e),
      role,
      ref: setRef,
      tabIndex: selected || activeItemIndex < 0 || role === 'link' ? 0 : -1,
    };

    const buttonContent = (
      <Styled.Inner>
        {miniBadge ? <Styled.MiniBadge /> : null}
        {Icon ? (
          <Styled.ButtonIcon>
            <Icon height={iconSize} width={iconSize} aria-hidden="true" />
          </Styled.ButtonIcon>
        ) : null}
        <span>
          {typeof children === 'string' ? clampButtonText(children) : children}
        </span>
      </Styled.Inner>
    );
    return role === 'link' || to || href ? (
      <Styled.RouterLink
        {...(to ? { to } : {})}
        {...(href ? { href } : {})}
        {...commonProps}
        {...props}
      >
        {buttonContent}
      </Styled.RouterLink>
    ) : (
      <Styled.Button {...commonProps} {...props}>
        {buttonContent}
      </Styled.Button>
    );
  }
);

ContentSwitcherButton.displayName = 'ContentSwitcherButton';
